import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { adminAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { hasBillingRole, hasAdminManagerRole } from '../../../utils/roles';
import { formatDate } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/LoadingSpinner';
import InventoryStylePagination from '../../../components/InventoryStylePagination';

// RF2: admin surface for storage units. The physical class is derived
// from measured dimensions (correcting dims re-derives it); the billing
// class can diverge only via the role-gated override with a mandatory
// reason. This page shows both truths side by side.
const CLASS_LABELS = {
  standard: 'Standard',
  rackable: 'Standard (legacy)',
  oversize: 'Oversize',
  floor_only: 'Floor Only',
  hazmat: 'Hazmat'
};

const CLASS_STYLES = {
  standard: 'bg-green-100 text-green-800',
  rackable: 'bg-green-100 text-green-800',
  oversize: 'bg-amber-100 text-amber-800',
  floor_only: 'bg-blue-100 text-blue-800',
  hazmat: 'bg-red-100 text-red-800'
};

const OVERRIDE_OPTIONS = ['standard', 'oversize', 'floor_only', 'hazmat'];

const ClassBadge = ({ storageClass }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CLASS_STYLES[storageClass] || 'bg-gray-100 text-gray-600'}`}>
    {CLASS_LABELS[storageClass] || storageClass || '-'}
  </span>
);

const formatDims = (unit) => {
  if (!unit.length_inches && !unit.width_inches && !unit.height_inches) return '-';
  const dim = (v) => (v ? parseFloat(v) : '?');
  return `${dim(unit.length_inches)} x ${dim(unit.width_inches)} x ${dim(unit.height_inches)}"`;
};

// Dimension-correction modal. Class is recalculated server-side, so the
// modal only explains that; it never offers a class picker.
const EditDimensionsModal = ({ unit, onClose, onSaved }) => {
  const [length, setLength] = useState(unit.length_inches ? String(parseFloat(unit.length_inches)) : '');
  const [width, setWidth] = useState(unit.width_inches ? String(parseFloat(unit.width_inches)) : '');
  const [height, setHeight] = useState(unit.height_inches ? String(parseFloat(unit.height_inches)) : '');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const values = { length_inches: length, width_inches: width, height_inches: height };
    for (const [field, value] of Object.entries(values)) {
      if (value !== '' && (!isFinite(parseFloat(value)) || parseFloat(value) <= 0)) {
        toast.error(`Invalid ${field.replace('_inches', '')} value`);
        return;
      }
    }
    setSaving(true);
    try {
      await adminAPI.updateStorageUnit(unit.id, {
        length_inches: length === '' ? null : parseFloat(length),
        width_inches: width === '' ? null : parseFloat(width),
        height_inches: height === '' ? null : parseFloat(height)
      });
      toast.success('Dimensions updated; physical class recalculated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update dimensions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="edit-dims-title">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 id="edit-dims-title" className="text-lg font-medium text-gray-900 mb-1">Correct Dimensions</h3>
        <p className="text-sm text-gray-500 mb-4">
          Outer dimensions in inches. The physical storage class is recalculated
          automatically: standard fits 40 x 48 and up to 60" tall, anything larger is oversize.
        </p>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[['Length', length, setLength], ['Width', width, setWidth], ['Height', height, setHeight]].map(([label, value, setter]) => (
            <div key={label}>
              <label className="block text-xs font-medium text-gray-700 mb-1">{label} (in)</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={value}
                onChange={(e) => setter(e.target.value)}
                className="w-full border border-gray-300 rounded-md py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Manual physical designation modal (admin/manager only). floor_only and
// hazmat are warehouse judgments the dimensions cannot express; clearing
// returns the unit to its dimension-derived class. Reason mandatory.
const PhysicalClassModal = ({ unit, onClose, onSaved }) => {
  const isManual = ['floor_only', 'hazmat'].includes(unit.storage_class);
  const [physicalClass, setPhysicalClass] = useState(isManual ? unit.storage_class : '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('A reason is required');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.setStorageUnitPhysicalClass(unit.id, {
        physicalClass: physicalClass || null,
        reason: reason.trim()
      });
      toast.success(physicalClass ? 'Physical designation applied' : 'Designation cleared; class re-derived from dimensions');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update physical designation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="physical-class-title">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 id="physical-class-title" className="text-lg font-medium text-gray-900 mb-1">Physical Designation</h3>
        <p className="text-sm text-gray-500 mb-4">
          Standard and oversize are always calculated from dimensions. Use this only for
          designations dimensions cannot express; clearing re-derives the class.
        </p>
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Designation</label>
          <select
            value={physicalClass}
            onChange={(e) => setPhysicalClass(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">None (derive from dimensions)</option>
            <option value="floor_only">Floor Only</option>
            <option value="hazmat">Hazmat</option>
          </select>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason (required)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-md py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="Why does this unit need a manual physical designation?"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Billing-class override modal (billing roles only). Reason is mandatory
// in both directions -- the concession trail must be auditable.
const OverrideModal = ({ unit, onClose, onSaved }) => {
  const [billingClass, setBillingClass] = useState(unit.billing_class_override || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('A reason is required');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.setStorageUnitBillingOverride(unit.id, {
        billingClass: billingClass || null,
        reason: reason.trim()
      });
      toast.success(billingClass ? 'Billing-class override applied' : 'Billing-class override cleared');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update override');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="override-title">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 id="override-title" className="text-lg font-medium text-gray-900 mb-1">Billing-Class Override</h3>
        <p className="text-sm text-gray-500 mb-4">
          Physical class stays <ClassBadge storageClass={unit.storage_class} /> — this only changes
          what the unit is billed as (e.g. physically oversized, but charge standard).
        </p>
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Bill as</label>
          <select
            value={billingClass}
            onChange={(e) => setBillingClass(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">No override (bill physical class)</option>
            {OVERRIDE_OPTIONS.map((cls) => (
              <option key={cls} value={cls}>{CLASS_LABELS[cls]}</option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason (required)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-md py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="Why is this unit billed differently from its physical class?"
          />
        </div>
        {unit.billing_class_override && unit.billing_class_override_reason && (
          <p className="text-xs text-gray-500 mb-4">
            Current override reason: {unit.billing_class_override_reason}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

const StorageUnitsList = () => {
  const { user } = useAuth();
  const canOverride = hasBillingRole(user);
  const canDesignate = hasAdminManagerRole(user);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [companyFilter, setCompanyFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('in_storage');
  const [editingUnit, setEditingUnit] = useState(null);
  const [overridingUnit, setOverridingUnit] = useState(null);
  const [designatingUnit, setDesignatingUnit] = useState(null);

  const { data: customersData } = useQuery(
    ['admin-customers-for-storage-units'],
    () => adminAPI.getCustomers({ limit: 500 }),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: warehousesData } = useQuery(
    ['admin-warehouses-for-storage-units'],
    () => adminAPI.getWarehouses(),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data, isLoading, refetch } = useQuery(
    ['admin-storage-units', page, limit, companyFilter, warehouseFilter, classFilter, statusFilter],
    () => adminAPI.getStorageUnits({
      page,
      limit,
      companyId: companyFilter || undefined,
      warehouseId: warehouseFilter || undefined,
      storageClass: classFilter || undefined,
      status: statusFilter || undefined
    }),
    { keepPreviousData: true }
  );

  const units = data?.data || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };
  const customers = customersData?.data || [];
  const warehouses = Array.isArray(warehousesData) ? warehousesData : (warehousesData?.data || []);

  const closeAndRefetch = () => {
    setEditingUnit(null);
    setOverridingUnit(null);
    setDesignatingUnit(null);
    refetch();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Storage Units</h1>
          <p className="mt-1 text-sm text-gray-500">
            Physical class is calculated from measured dimensions. Billing follows the
            physical class unless a billing-class override is applied.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={companyFilter}
          onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All customers</option>
          {customers.map((company) => (
            <option key={company.id} value={company.id}>{company.name}</option>
          ))}
        </select>
        <select
          value={warehouseFilter}
          onChange={(e) => { setWarehouseFilter(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All warehouses</option>
          {warehouses.map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
          ))}
        </select>
        <select
          value={classFilter}
          onChange={(e) => { setClassFilter(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All classes</option>
          <option value="standard">Standard</option>
          <option value="oversize">Oversize</option>
          <option value="floor_only">Floor Only</option>
          <option value="hazmat">Hazmat</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="in_storage">In storage</option>
          <option value="shipped">Shipped</option>
          <option value="">All</option>
        </select>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : units.length === 0 ? (
        <p className="text-gray-500 text-center py-12 bg-white rounded-lg border border-gray-200">
          No storage units match these filters
        </p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Warehouse</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dims</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Physical Class</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Billing Class</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Pallet Eq.</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {units.map((unit) => (
                <tr key={unit.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-sm text-gray-900">{unit.Company?.name || '-'}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{unit.Warehouse?.name || '-'}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 capitalize">
                    {unit.unit_type}{unit.unit_type === 'carton' && unit.quantity > 1 ? ` x ${unit.quantity}` : ''}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{formatDims(unit)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <ClassBadge storageClass={unit.storage_class} />
                    {unit.storage_class_defaulted && (
                      <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500" title="Classified without measured dimensions — correct the dims to recalculate">
                        defaulted
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <ClassBadge storageClass={unit.effective_billing_class || unit.storage_class} />
                    {unit.billing_class_override && (
                      <span
                        className="ml-1 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700"
                        title={unit.billing_class_override_reason || 'Billing-class override applied'}
                      >
                        override
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 text-right">
                    {Number(unit.effective_pallet_equivalent || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{formatDate(unit.received_date)}</td>
                  <td className="px-4 py-2.5 text-sm text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditingUnit(unit)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                    >
                      Dims
                    </button>
                    {canDesignate && (
                      <button
                        onClick={() => setDesignatingUnit(unit)}
                        className="text-gray-600 hover:text-gray-800 mr-3"
                        title="Manual physical designation (floor only / hazmat)"
                      >
                        Class
                      </button>
                    )}
                    {canOverride && (
                      <button
                        onClick={() => setOverridingUnit(unit)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        Override
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <InventoryStylePagination
          currentPage={page}
          totalPages={pagination.pages || 1}
          totalItems={pagination.total || 0}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
        />
      </div>

      {editingUnit && (
        <EditDimensionsModal unit={editingUnit} onClose={() => setEditingUnit(null)} onSaved={closeAndRefetch} />
      )}
      {overridingUnit && (
        <OverrideModal unit={overridingUnit} onClose={() => setOverridingUnit(null)} onSaved={closeAndRefetch} />
      )}
      {designatingUnit && (
        <PhysicalClassModal unit={designatingUnit} onClose={() => setDesignatingUnit(null)} onSaved={closeAndRefetch} />
      )}
    </div>
  );
};

export default StorageUnitsList;
